/**
 * block-registry.ts — ONE declaration of what a block is and where it may be
 * used (2026-08-31, stage 4 of the editor-unification plan).
 *
 * Before this there were two block libraries with no relationship: ten
 * Outlook-survivable types in email-model.ts and sixteen modern-CSS types in
 * nj-model.ts. The operator's ruling was one library where each block declares
 * its targets, and the palette filters by the artifact's target — so an email
 * author never sees a Video block to be warned about, because it is not in the
 * room. Absence, not a warning.
 *
 * DEPTH (my call, the question came back unanswered): this registry is
 * METADATA ONLY. It declares the label, the targets, the per-target variant and
 * the hint. It does NOT own the renderers or the stored block shape, because
 * those genuinely differ: an email block is `{ uid, type, cfg }` rendered
 * through email-render.ts's nested tables, a journey block is
 * `{ uid, type, ...keys }` rendered as modern CSS. Merging those would migrate
 * live documents on both sides and break the "output does not move" ruling that
 * governs this whole piece of work. The gate is real either way; pulling the
 * renderers in is a separate stage if it is ever worth it.
 *
 * ON THE ID AUDIT (operator ruling: rename where ids collide with different
 * meanings, migrating stored documents). I audited all 26 types. Five ids are
 * shared — text, image, button, divider, spacer — and in every case the two
 * targets mean the SAME thing and differ only in cfg keys, which the hosts
 * already own. The one apparent collision is `columns`: a fixed left/right pair
 * in email, a container on a page. That is not two meanings, it is one concept
 * with two implementations, which is what `variant` below records and what the
 * operator already ruled on ("same block in the palette, same name, different
 * inspector"). So NOTHING is renamed and NOTHING stored is migrated. Said out
 * loud because a ruling that turns out to have no work in it should be reported,
 * not quietly skipped.
 */

import { t as i18nT } from "../../lib/i18n";

export type BlockTarget = "email" | "page";

/** v1 ships no container blocks (operator ruling 2026-09-01, CD prompt §6a). A
 *  `v2` type stays DECLARED here — removing it and re-adding it later would
 *  migrate live documents twice, and a stored `columns` block must keep
 *  rendering — but it is absent from every v1 palette. Same principle as the
 *  target gate: absence, not a warning. */
export type BlockTier = "v1" | "v2";

export interface SharedBlockDef {
  type: string;
  label: string;
  /** i18n key, where the host already had one. */
  labelKey?: string;
  /** THE GATE. A target absent here is absent from that target's palette. */
  targets: BlockTarget[];
  /** Palette tier. Absent = v1. A `v2` block renders but cannot be added. */
  tier?: BlockTier;
  /** One line, per target where they differ. */
  hint: string | Partial<Record<BlockTarget, string>>;
  /** Why the same type renders differently on each target. */
  variant?: Partial<Record<BlockTarget, string>>;
  /** May appear more than once on the same page/message. */
  repeat?: boolean;
}

/** Order matters: this is palette order on both targets. */
export const BLOCK_REGISTRY: SharedBlockDef[] = [
  /* ── shared ─────────────────────────────────────────────────────── */
  { type: "text", label: "Text", labelKey: "messaging.email.block.text.label", targets: ["email", "page"],
    hint: { email: "Paragraph — supports {first_name}", page: "Paragraph of copy" } },
  { type: "image", label: "Image", labelKey: "messaging.email.block.image.label", targets: ["email", "page"],
    hint: "Hosted image by URL" },
  { type: "button", label: "Button", labelKey: "messaging.email.block.button.label", targets: ["email", "page"],
    hint: { email: "Bulletproof CTA (VML in Outlook)", page: "Link or page jump" },
    variant: { email: "VML shape, so Outlook keeps it a solid clickable block", page: "An anchor, with hover and focus states" } },
  { type: "columns", label: "Two columns", labelKey: "messaging.email.block.columns.label", targets: ["email", "page"], tier: "v2",
    hint: { email: "Fixed pair — stacks on mobile", page: "Container — drop any block into a column" },
    variant: {
      email: "A fixed left/right title-and-text pair. Arbitrary nesting inside a two-column table is where Outlook's column widths collapse, so the email variant does not offer it.",
      page: "A real container: any block can be dragged into either column.",
    } },
  { type: "divider", label: "Divider", labelKey: "messaging.email.block.divider.label", targets: ["email", "page"], repeat: true,
    hint: "Hairline rule" },
  { type: "spacer", label: "Spacer", labelKey: "messaging.email.block.spacer.label", targets: ["email", "page"], repeat: true,
    hint: "Vertical breathing room" },

  /* ── email only ─────────────────────────────────────────────────── */
  { type: "logo", label: "Logo / brand", labelKey: "messaging.email.block.logo.label", targets: ["email"],
    hint: "Brand name, or an image URL" },
  { type: "heading", label: "Heading", labelKey: "messaging.email.block.heading.label", targets: ["email"],
    hint: "Large or medium title line" },
  { type: "footer", label: "Footer", labelKey: "messaging.email.block.footer.label", targets: ["email"],
    hint: "Address + unsubscribe (required)" },
  { type: "code", label: "Custom HTML", labelKey: "messaging.email.block.code.label", targets: ["email"],
    hint: "Raw fragment — verbatim in every client" },

  /* ── artefact and live-data email blocks (CD prompt §6b, 2026-09-01) ──
     All four were already rendered server-side, with Outlook handling, and
     missing only from this palette — so an author could not reach them. For an
     events product attendee-QR and calendar-link are not decoration: they are
     how a guest gets into the venue and onto their calendar. */
  { type: "calendar_link", get label() { return i18nT("shared.blocks.calendar_link.label", "Calendar links"); }, targets: ["email"],
    get hint() { return i18nT("shared.blocks.calendar_link.hint", "Add to calendar — .ics, Google and Apple"); },
    variant: { email: "Renders all THREE targets side by side. A Button pointing at the calendar token is the single-CTA alternative; keep both — they answer different authoring intents." } },
  /* The contract calls these `email_qr` and `email_attendee_qr` and labels BOTH
     "Email QR". Two types with one operator-facing name is unusable in a
     palette, so this proposes the pair by WHOSE credential each one carries —
     the recipient's, or one row per named attendee. */
  { type: "qr", get label() { return i18nT("shared.blocks.qr.label", "Booking QR"); }, targets: ["email"],
    get hint() { return i18nT("shared.blocks.qr.hint", "The recipient's own check-in code"); } },
  { type: "attendee_qr", get label() { return i18nT("shared.blocks.attendee_qr.label", "Attendee QR"); }, targets: ["email"], repeat: true,
    get hint() { return i18nT("shared.blocks.attendee_qr.hint", "One code per named attendee"); } },

  /* ── page only. Every one of these is modern CSS, live data or
       interactive, so none survives Outlook's Word engine. ────────── */
  { type: "notice", get label() { return i18nT("shared.blocks.notice.label", "Notice"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.notice.hint", "Tinted callout"); } },
  { type: "hero", get label() { return i18nT("shared.blocks.hero.label", "Hero"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.hero.hint", "Full-bleed header — pinned to the top"); } },
  { type: "video", get label() { return i18nT("shared.blocks.video.label", "Video"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.video.hint", "Embedded player"); } },
  { type: "gallery", get label() { return i18nT("shared.blocks.gallery.label", "Gallery"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.gallery.hint", "Grid of images"); } },
  { type: "map", get label() { return i18nT("shared.blocks.map.label", "Map"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.map.hint", "Live from Setup · Details"); } },
  { type: "faq", get label() { return i18nT("shared.blocks.faq.label", "FAQ"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.faq.hint", "Question and answer list"); } },
  { type: "quote", get label() { return i18nT("shared.blocks.quote.label", "Quote"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.quote.hint", "Pull quote"); } },
  { type: "stats", get label() { return i18nT("shared.blocks.stats.label", "Stats"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.stats.hint", "Row of numbers"); } },
  { type: "countdown", get label() { return i18nT("shared.blocks.countdown.label", "Countdown"); }, targets: ["email", "page"], get hint() { return i18nT("shared.blocks.countdown.hint", "Runs to the event's start"); },
    variant: { email: "A server-rendered image, not live CSS — Outlook's Word engine animates nothing." } },
  { type: "social", get label() { return i18nT("shared.blocks.social.label", "Social"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.social.hint", "Icon row"); } },
  { type: "eventdetails", get label() { return i18nT("shared.blocks.eventdetails.label", "Event details"); }, targets: ["page"], get hint() { return i18nT("shared.blocks.eventdetails.hint", "Live from Setup · Schedule and Details"); } },
];

export const blockDef = (type: string): SharedBlockDef | null =>
  BLOCK_REGISTRY.filter((b) => b.type === type)[0] ?? null;

/** Palette ORDER per target. The registry is grouped by target for reading;
 *  each palette keeps the order its operators already have muscle memory for,
 *  which deriving the list from a single grouped array would have silently
 *  changed on both sides. Anything absent from a list falls to the end. */
const PALETTE_ORDER: Record<BlockTarget, string[]> = {
  email: ["logo", "heading", "text", "button", "image", "calendar_link", "qr", "attendee_qr", "countdown", "divider", "spacer", "footer", "code"],
  page: ["text", "notice", "button", "image", "hero", "video", "gallery", "map", "faq", "quote", "stats", "countdown", "social", "eventdetails", "divider", "spacer"],
};

export const blockTier = (b: SharedBlockDef): BlockTier => b.tier ?? "v1";

/** The v1 palette for a target. `includeV2` exists for the v2 palette work and
 *  for an inspector that must still describe a stored v2 block — no caller
 *  should pass it to build a palette today. */
export const blocksForTarget = (target: BlockTarget, opts?: { includeV2?: boolean }): SharedBlockDef[] => {
  const order = PALETTE_ORDER[target];
  const rank = (t: string) => { const i = order.indexOf(t); return i === -1 ? order.length : i; };
  return BLOCK_REGISTRY
    .filter((b) => b.targets.indexOf(target) > -1)
    .filter((b) => opts?.includeV2 || blockTier(b) === "v1")
    .slice()
    .sort((a, b) => rank(a.type) - rank(b.type));
};

export const blockHint = (b: SharedBlockDef, target: BlockTarget): string =>
  (typeof b.hint === "string" ? b.hint : b.hint[target] ?? "");

export const blockVariant = (type: string, target: BlockTarget): string | null => {
  const d = blockDef(type);
  return d?.variant?.[target] ?? null;
};

/** Types that hold other blocks. There was a `nestable` flag here saying which
 *  blocks a cell would accept; it was removed because nothing enforced it — the
 *  page container takes any block today, and metadata that states a rule no code
 *  applies is worse than none: the next reader believes it. Add it back with the
 *  check that reads it, or not at all. */
export const CONTAINER_TYPES = ["columns"];
export const isContainer = (type: string): boolean => CONTAINER_TYPES.indexOf(type) > -1;
